import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import RefreshIcon from "@material-ui/icons/Refresh";
import BusinessIcon from "@material-ui/icons/Business";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PaymentIcon from "@material-ui/icons/Payment";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import GroupIcon from "@material-ui/icons/Group";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import ImageIcon from "@material-ui/icons/Image";
import CloseIcon from "@material-ui/icons/Close";
import VisibilityIcon from "@material-ui/icons/Visibility";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import ShoppingCartOutlinedIcon from "@material-ui/icons/ShoppingCartOutlined";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { useDialogStyles } from "../../theme/redesign";
import {
  ContactAvatar,
  EmptyHint,
  FilterChips,
  KpiTile,
  NegocioPager,
  PageFrame,
  RecordsState,
  SearchField,
  tNegocio,
  useMobile,
  usePager,
} from "./shared";
import { useStyles as useNegocioStyles } from "./styles";
import { ChatBubbleButton, useCustomerChat } from "./CustomerChatButton";
import { setPublicidadDraft } from "./publicidadDraft";

const t = (key, vars) => {
  let text = tNegocio(`pages.watoolx.${key}`);
  if (vars) Object.entries(vars).forEach(([k, v]) => { text = text.split(`{${k}}`).join(String(v)); });
  return text;
};

const STATE_TONE = {
  active: "#2e7d32",
  due_soon: "#c2410c",
  expired: "#c62828",
  trial: "#6d28d9",
  suspended: "#64748b",
};
const STATE_ORDER = { due_soon: 0, expired: 1, active: 2, trial: 3, suspended: 4 };
const MONTH_OPTIONS = [1, 2, 3, 6, 12];

const usd = (n) => `USD ${Number(n || 0).toLocaleString("es-CO", { minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
const dmy = (day) => (day ? moment(day, "YYYY-MM-DD").format("DD/MM/YYYY") : "—");
const extendDueDay = (currentDue, months, today) => {
  const base = currentDue && currentDue >= today ? currentDue : today;
  return moment(base, "YYYY-MM-DD").add(months, "months").format("YYYY-MM-DD");
};

const useStyles = makeStyles((theme) => ({
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 2.2fr) minmax(120px, 1fr) minmax(150px, 1.2fr) minmax(130px, 1fr) auto",
    alignItems: "center",
    gap: 14,
    padding: "12px 14px",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: 10,
    },
  },
  identity: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
  name: { fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  caption: { color: theme.palette.text.secondary, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  planChip: { fontWeight: 700 },
  stateLine: { display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13 },
  dot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  usage: { fontSize: 13, lineHeight: 1.5 },
  actions: { display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end", [theme.breakpoints.down("sm")]: { justifyContent: "flex-start" } },
  planBar: { display: "flex", flexWrap: "wrap", gap: 6, margin: "2px 0 10px" },
  summary: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 10,
    background: theme.mode === "dark" ? "rgba(14,165,233,0.12)" : "#f0f9ff",
    border: `1px solid ${theme.mode === "dark" ? "rgba(14,165,233,0.35)" : "#bae6fd"}`,
    fontSize: 13.5,
    lineHeight: 1.6,
  },
  warn: { color: "#c2410c", fontSize: 12.5, marginTop: 4 },
  fieldRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
  destGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginTop: 8, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
  dest: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    "&:hover": { borderColor: theme.palette.primary.main },
    "&:disabled": { opacity: 0.6, cursor: "default" },
  },
  destTitle: { fontWeight: 800, fontSize: 14 },
  destHint: { color: theme.palette.text.secondary, fontSize: 12 },
  thumb: { width: 64, height: 64, objectFit: "cover", borderRadius: 10, border: `1px solid ${theme.palette.divider}` },
  imageRow: { display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" },
  sectionTitle: { fontWeight: 800, fontSize: 14, margin: "14px 0 4px" },
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    background: theme.palette.background.paper,
    padding: "10px 14px",
    marginBottom: 10,
    flexShrink: 0,
  },
  panelHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  panelTitle: { fontWeight: 800, fontSize: 14.5, display: "inline-flex", alignItems: "center", gap: 6 },
  productGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8, marginTop: 8 },
  productItem: { border: `1px solid ${theme.palette.divider}`, borderRadius: 10, padding: "8px 10px", fontSize: 13 },
  flags: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 },
  orderRow: {
    display: "grid",
    gridTemplateColumns: "minmax(200px, 1.6fr) minmax(140px, 1fr) minmax(220px, 1.4fr)",
    gap: 10,
    alignItems: "center",
    padding: "8px 0",
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  orderActions: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  hiddenBar: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 10, fontSize: 12.5, opacity: 0.85 },
}));

function StateLine({ row }) {
  const classes = useStyles();
  const color = STATE_TONE[row.state] || "#64748b";
  let text = t(`stateOne.${row.state}`);
  if (row.daysLeft != null && row.state !== "suspended") {
    if (row.daysLeft < 0) text = t("due.expiredN", { n: -row.daysLeft });
    else if (row.daysLeft === 0) text = t("due.today");
    else if (row.state === "due_soon") text = t("due.leftN", { n: row.daysLeft });
  }
  return (
    <span className={classes.stateLine} style={{ color }}>
      <span className={classes.dot} style={{ background: color }} />
      {text}
    </span>
  );
}

function LinesUsage({ row }) {
  const classes = useStyles();
  const detail = row.lines.length
    ? row.lines.map((l) => `${l.type === "telegram" ? "Telegram" : "WhatsApp"} · ${l.name || "—"} · ${l.status === "CONNECTED" ? t("connected") : t("disconnected")}`).join("\n")
    : t("noLines");
  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{detail}</span>}>
      <div className={classes.usage}>
        <div>{t("linesUsage", { on: row.linesConnected, total: row.lines.length, max: row.maxLines ?? "—" })}</div>
        <div className={classes.caption}>{t("usersUsage", { n: row.users, max: row.maxUsers ?? "—" })}</div>
      </div>
    </Tooltip>
  );
}

function PaymentDialog({ row, data, onClose, onDone }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const mobile = useMobile();
  const [months, setMonths] = useState(1);
  const [planId, setPlanId] = useState(row.planId || "");
  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [methodId, setMethodId] = useState("");
  const [note, setNote] = useState("");
  const [recordSale, setRecordSale] = useState(!!row.contact);
  const [notify, setNotify] = useState(!!row.phone);
  const [busy, setBusy] = useState(false);

  const plan = data.plans.find((p) => p.id === Number(planId)) || null;
  // Mismo plan: se respeta el precio de la factura del ciclo (puede ser un precio anterior).
  const unit = plan && Number(planId) === Number(row.planId) && row.cycleInvoice ? row.cycleInvoice.value : plan ? plan.value : 0;
  useEffect(() => {
    if (!amountTouched) setAmount(String(Math.round(unit * months * 100) / 100));
  }, [unit, months, amountTouched]);

  const newDue = extendDueDay(row.dueDay, months, data.today);
  const rate = Number(data.negocio?.exchangeRate || 0);
  const local = Number(amount || 0) * rate;
  const localLabel = rate > 0
    ? `${data.negocio?.currencySymbol || "$"} ${Math.round(local).toLocaleString("es-CO")} ${data.negocio?.currencyLocal || ""}`.trim()
    : null;
  const valid = plan && Number(amount) >= 0 && amount !== "";

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/platform/clients/${row.id}/payment`, {
        months,
        planId: Number(planId),
        amount: Number(amount),
        paymentMethodId: methodId || null,
        note,
        recordSale,
        notify,
      });
      toast.success(t("pay.done", { name: row.name, date: dmy(result.dueDay) }));
      if (result.saleSkipped && recordSale) toast.warn(result.saleSkipped);
      if (notify && result.notified === false) toast.warn(t("pay.notNotified"));
      onDone();
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullScreen={mobile} fullWidth maxWidth="sm" PaperProps={{ className: dlg.paper }}>
      <DialogTitle>{t("pay.title", { name: row.name })}</DialogTitle>
      <DialogContent>
        <div className={classes.fieldRow} style={{ marginTop: 0 }}>
          <TextField select label={t("pay.months")} variant="outlined" size="small" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            {MONTH_OPTIONS.map((m) => (
              <MenuItem key={m} value={m}>{m === 1 ? t("pay.oneMonth") : t("pay.nMonths", { n: m })}</MenuItem>
            ))}
          </TextField>
          <TextField select label={t("pay.plan")} variant="outlined" size="small" value={planId} onChange={(e) => { setPlanId(Number(e.target.value)); setAmountTouched(false); }}>
            {data.plans.map((p) => (
              <MenuItem key={p.id} value={p.id}>{`${p.name} · ${usd(p.value)}`}</MenuItem>
            ))}
          </TextField>
        </div>
        <div className={classes.fieldRow}>
          <TextField
            label={t("pay.amount")}
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            value={amount}
            onChange={(e) => { setAmount(e.target.value); setAmountTouched(true); }}
            InputProps={{ startAdornment: <InputAdornment position="start">USD</InputAdornment> }}
            helperText={localLabel ? t("pay.equals", { value: localLabel }) : undefined}
          />
          <TextField select label={t("pay.method")} variant="outlined" size="small" value={methodId} onChange={(e) => setMethodId(e.target.value)}>
            <MenuItem value="">{t("pay.noMethod")}</MenuItem>
            {data.paymentMethods.map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
            ))}
          </TextField>
        </div>
        <TextField
          fullWidth
          label={t("pay.note")}
          variant="outlined"
          size="small"
          style={{ marginTop: 12 }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 300 }}
        />
        <div style={{ marginTop: 8 }}>
          <FormControlLabel
            control={<Switch color="primary" checked={recordSale} disabled={!row.contact} onChange={(e) => setRecordSale(e.target.checked)} />}
            label={t("pay.recordSale")}
          />
          {!row.contact ? <div className={classes.warn}>{t("pay.noContact")}</div> : null}
          <FormControlLabel
            control={<Switch color="primary" checked={notify} disabled={!row.phone} onChange={(e) => setNotify(e.target.checked)} />}
            label={t("pay.notify")}
          />
        </div>
        <div className={classes.summary}>
          <div>{t("pay.dueChange", { from: dmy(row.dueDay), to: dmy(newDue) })}</div>
          {plan && Number(planId) !== Number(row.planId) ? <div>{t("pay.planChange", { from: row.planName || "—", to: plan.name })}</div> : null}
          {row.cycleInvoice ? <div>{t("pay.invoice", { id: row.cycleInvoice.id })}</div> : <div>{t("pay.noInvoice")}</div>}
          {row.state === "suspended" ? <div>{t("pay.reactivate")}</div> : null}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t("cancel")}</Button>
        <Button variant="contained" color="primary" onClick={submit} disabled={!valid || busy} startIcon={busy ? <CircularProgress size={16} /> : <PaymentIcon />}>
          {t("pay.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PlanDialog({ row, plans, onClose, onDone }) {
  const dlg = useDialogStyles();
  const [planId, setPlanId] = useState(row.planId || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/platform/clients/${row.id}/plan`, { planId: Number(planId) });
      toast.success(t("plan.done", { name: row.name, plan: data.planName }));
      onDone();
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{ className: dlg.paper }}>
      <DialogTitle>{t("plan.title", { name: row.name })}</DialogTitle>
      <DialogContent>
        <TextField select fullWidth label={t("pay.plan")} variant="outlined" size="small" value={planId} onChange={(e) => setPlanId(Number(e.target.value))}>
          {plans.map((p) => (
            <MenuItem key={p.id} value={p.id}>{`${p.name} · ${usd(p.value)} · ${t("plan.limits", { users: p.users, lines: p.connections })}`}</MenuItem>
          ))}
        </TextField>
        <div style={{ fontSize: 12.5, marginTop: 10, opacity: 0.8 }}>{t("plan.hint")}</div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t("cancel")}</Button>
        <Button variant="contained" color="primary" onClick={submit} disabled={busy || !planId || Number(planId) === Number(row.planId)}>
          {t("plan.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const FEATURE_KEYS = ["useGroupCampaigns", "useStatusCampaigns", "useCampaigns", "useNegocio", "useOpenAi", "useSchedules", "useKanban", "useInternalChat", "useIntegrations", "useExternalApi"];

/** Texto de publicidad a partir de los planes elegidos: cada plan muestra lo que suma sobre el anterior. */
export function buildPromoText(plans, signupUrl, withTrial) {
  const paid = [...plans].sort((a, b) => a.value - b.value);
  const lines = [t("promo.headline"), "", t("promo.intro"), ""];
  const all = new Set(paid.flatMap((p) => p.features || []));
  FEATURE_KEYS.filter((k) => all.has(k)).slice(0, 6).forEach((k) => lines.push(`✅ ${t(`feature.${k}`)}`));
  lines.push("", t("promo.plansTitle"));
  let prev = null;
  paid.forEach((p) => {
    const extra = (p.features || []).filter((k) => FEATURE_KEYS.includes(k) && !(prev?.features || []).includes(k));
    const limits = t("promo.limits", { users: p.users, lines: p.connections });
    const plus = prev && extra.length ? ` · ${extra.slice(0, 3).map((k) => t(`featureShort.${k}`)).join(", ")}` : "";
    lines.push(`• *${p.name}* — ${usd(p.value)}${t("promo.perMonth")} · ${limits}${plus}`);
    prev = p;
  });
  if (withTrial) lines.push("", t("promo.trial"));
  if (signupUrl) lines.push("", t("promo.cta", { url: signupUrl }));
  return lines.join("\n");
}

const DESTINATIONS = [
  { id: "groups", path: "/group-campaigns", icon: GroupIcon, title: "dest.groups", hint: "dest.groupsHint" },
  { id: "status", path: "/status-campaigns", icon: PhoneAndroidIcon, title: "dest.status", hint: "dest.statusHint" },
  { id: "campaigns", path: "/campaigns", icon: AnnouncementIcon, title: "dest.campaigns", hint: "dest.campaignsHint" },
];

function PromoDialog({ data, onClose }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const mobile = useMobile();
  const history = useHistory();
  const paidPlans = useMemo(() => data.plans.filter((p) => p.value > 0), [data.plans]);
  const hasTrial = data.plans.some((p) => !(p.value > 0));
  const [picked, setPicked] = useState(() => new Set(paidPlans.map((p) => p.id)));
  const [withTrial, setWithTrial] = useState(hasTrial);
  const [text, setText] = useState("");
  const [touched, setTouched] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [recurring, setRecurring] = useState(false);

  const generated = useMemo(
    () => buildPromoText(paidPlans.filter((p) => picked.has(p.id)), data.signupUrl, withTrial),
    [paidPlans, picked, data.signupUrl, withTrial]
  );
  useEffect(() => { if (!touched) setText(generated); }, [generated, touched]);
  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const sendTo = (dest) => {
    if (!text.trim()) return;
    setPublicidadDraft({
      title: t("promo.campaignName", { date: moment().format("DD/MM") }),
      text,
      file: file || null,
      destination: dest.id,
      campaignType: recurring ? "RECURRENTE" : "UNICA",
    });
    toast.success(t("promo.opening"));
    onClose();
    history.push(dest.path);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("promo.copied"));
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Dialog open onClose={onClose} fullScreen={mobile} fullWidth maxWidth="md" PaperProps={{ className: dlg.paper }}>
      <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontWeight: 800, fontSize: 18 }}>{t("promo.title")}</span>
        <IconButton onClick={onClose} aria-label={t("cancel")}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <div className={classes.sectionTitle} style={{ marginTop: 0 }}>{t("promo.plans")}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {paidPlans.map((p) => (
            <FormControlLabel
              key={p.id}
              control={<Checkbox color="primary" checked={picked.has(p.id)} onChange={() => { toggle(p.id); setTouched(false); }} />}
              label={`${p.name} · ${usd(p.value)}`}
            />
          ))}
          {hasTrial ? (
            <FormControlLabel
              control={<Checkbox color="primary" checked={withTrial} onChange={(e) => { setWithTrial(e.target.checked); setTouched(false); }} />}
              label={t("promo.trialToggle")}
            />
          ) : null}
        </div>
        <TextField
          fullWidth
          multiline
          rows={mobile ? 10 : 12}
          variant="outlined"
          label={t("promo.text")}
          style={{ marginTop: 8 }}
          value={text}
          onChange={(e) => { setText(e.target.value); setTouched(true); }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
          <Button size="small" startIcon={<FileCopyIcon />} onClick={copy} disabled={!text}>{t("promo.copy")}</Button>
          <Button size="small" disabled={!touched} onClick={() => setTouched(false)}>{t("promo.reset")}</Button>
          <span style={{ fontSize: 12, opacity: 0.7 }}>{t("promo.chars", { n: text.length })}</span>
        </div>
        <div className={classes.imageRow}>
          <Button size="small" variant="outlined" component="label" startIcon={<ImageIcon />}>
            {file ? t("promo.changeImage") : t("promo.addImage")}
            <input hidden type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Button>
          {preview ? <img className={classes.thumb} src={preview} alt="" /> : <span style={{ fontSize: 12, opacity: 0.7 }}>{t("promo.imageHint")}</span>}
          {file ? <Button size="small" onClick={() => setFile(null)}>{t("promo.removeImage")}</Button> : null}
        </div>
        <div className={classes.sectionTitle}>{t("promo.useIn")}</div>
        <FormControlLabel
          control={<Switch color="primary" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />}
          label={t("promo.recurring")}
        />
        <div className={classes.destGrid}>
          {DESTINATIONS.map((dest) => {
            const Icon = dest.icon;
            return (
              <button key={dest.id} type="button" className={classes.dest} disabled={!text.trim()} onClick={() => sendTo(dest)}>
                <Icon color="primary" />
                <span>
                  <div className={classes.destTitle}>{t(dest.title)}</div>
                  <div className={classes.destHint}>{t(dest.hint)}</div>
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StoreProductsCard({ data, onChanged }) {
  const classes = useStyles();
  const history = useHistory();
  const [busy, setBusy] = useState(false);
  const items = data.storeProducts || [];
  const missing = items.filter((i) => !i.product).length;
  const symbol = data.negocio?.currencySymbol || "$";
  const create = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/platform/clients/store-products");
      toast.success(res.created?.length ? t("store.created", { n: res.created.length }) : t("store.linked"));
      onChanged();
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };
  const flag = (on, label) => <Chip size="small" label={label} color={on ? "primary" : "default"} variant={on ? "default" : "outlined"} style={on ? undefined : { textDecoration: "line-through" }} />;
  return (
    <div className={classes.panel}>
      <div className={classes.panelHead}>
        <span className={classes.panelTitle}><StorefrontOutlinedIcon fontSize="small" color="primary" />{t("store.title")}</span>
        <span style={{ display: "flex", gap: 6 }}>
          {missing ? (
            <Button size="small" variant="contained" color="primary" disabled={busy} onClick={create} startIcon={busy ? <CircularProgress size={14} /> : null}>
              {t("store.create", { n: missing })}
            </Button>
          ) : null}
          <Button size="small" onClick={() => history.push("/negocio/productos")}>{t("store.edit")}</Button>
        </span>
      </div>
      <div className={classes.caption} style={{ whiteSpace: "normal", marginTop: 2 }}>{missing ? t("store.hintMissing") : t("store.hint")}</div>
      <div className={classes.productGrid}>
        {items.map((i) => (
          <div key={i.planId} className={classes.productItem}>
            <div style={{ fontWeight: 800 }}>{i.product ? `${i.product.name} · ${i.planName}` : t("store.notCreated", { plan: i.planName })}</div>
            {i.product ? (
              <>
                <div className={classes.caption}>
                  {i.product.priceFx ? `${usd(i.product.priceFx)} · ` : ""}{`${symbol} ${Math.round(i.product.price).toLocaleString("es-CO")}`}
                </div>
                <div className={classes.flags}>
                  {flag(i.product.isActive, t("store.active"))}
                  {flag(i.product.botSellable, t("store.bot"))}
                  {flag(i.product.sellToReseller, t("store.resellers"))}
                  {flag(i.product.sellToFinal, t("store.finals"))}
                </div>
              </>
            ) : (
              <div className={classes.caption}>{t("store.planPrice", { value: usd(i.planValue) })}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const ORDER_STATUS = { awaiting_payment: "awaiting", payment_review: "review", paid: "paid", manual_activation: "toActivate" };

function OrderLine({ order, companies, onChanged }) {
  const classes = useStyles();
  const [companyId, setCompanyId] = useState(order.matches.length === 1 ? order.matches[0].id : "");
  const [busy, setBusy] = useState(false);
  const ready = order.status === "manual_activation";
  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/clients/orders/${order.id}/activate`, { companyId: Number(companyId) });
      toast.success(t("orders.done", { name: data.company?.name || "", date: dmy(data.dueDay) }));
      onChanged();
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={classes.orderRow}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 800 }}>{`#${order.id} · ${order.productName}`}</div>
        <div className={classes.caption}>{[order.contact.name, order.contact.number].filter(Boolean).join(" · ")}</div>
      </div>
      <div>
        <div style={{ fontWeight: 700, color: ready ? "#c2410c" : undefined }}>{t(`orders.status.${ORDER_STATUS[order.status] || "awaiting"}`)}</div>
        <div className={classes.caption}>
          {order.matches.length === 1
            ? t("orders.match", { name: order.matches[0].name })
            : order.matches.length > 1
              ? t("orders.manyMatches", { n: order.matches.length })
              : order.signupSent ? t("orders.signupSent") : t("orders.noAccount")}
        </div>
      </div>
      <div className={classes.orderActions}>
        {ready ? (
          <>
            <TextField select size="small" variant="outlined" label={t("orders.company")} value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={{ minWidth: 170 }}>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{`${c.name} #${c.id}`}</MenuItem>
              ))}
            </TextField>
            <Button size="small" variant="contained" color="primary" disabled={!companyId || busy} onClick={activate}>
              {t("orders.activate")}
            </Button>
          </>
        ) : (
          <span className={classes.caption}>{t("orders.waiting")}</span>
        )}
      </div>
    </div>
  );
}

function OrdersCard({ data, onChanged }) {
  const classes = useStyles();
  const companies = useMemo(
    () => [...(data.companies || []).map((c) => ({ id: c.id, name: c.name })), ...(data.hidden || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data.companies, data.hidden]
  );
  const toActivate = data.orders.filter((o) => o.status === "manual_activation").length;
  return (
    <div className={classes.panel}>
      <div className={classes.panelHead}>
        <span className={classes.panelTitle}><ShoppingCartOutlinedIcon fontSize="small" color="primary" />{t("orders.title")}</span>
        {toActivate ? <Chip size="small" style={{ background: "#fff7ed", color: "#c2410c", fontWeight: 700 }} label={t("orders.toActivate", { n: toActivate })} /> : null}
      </div>
      <div className={classes.caption} style={{ whiteSpace: "normal", marginTop: 2 }}>{t("orders.hint")}</div>
      {data.orders.map((o) => (
        <OrderLine key={o.id} order={o} companies={companies} onChanged={onChanged} />
      ))}
    </div>
  );
}

function ClientRow({ row, index, onPay, onPlan, onHide }) {
  const classes = useStyles();
  const negocio = useNegocioStyles();
  const { openChat, busy, modal } = useCustomerChat();
  const [menu, setMenu] = useState(null);
  const caption = [`#${row.id}`, row.phone, row.email].filter(Boolean).join(" · ");
  return (
    <div className={negocio.serviceCustomerCard} style={{ animationDelay: `${Math.min(index, 5) * 35}ms`, borderLeftColor: STATE_TONE[row.state] }}>
      <div className={classes.row}>
        <div className={classes.identity}>
          <ContactAvatar name={row.name} number={row.contact?.number || row.phone} profilePicUrl={row.contact?.profilePicUrl} />
          <div style={{ minWidth: 0 }}>
            <div className={classes.name} title={row.name}>{row.name || `#${row.id}`}</div>
            <div className={classes.caption} title={caption}>{caption}</div>
            {row.contact ? <div className={classes.caption}>{t("contact", { name: row.contact.name || row.contact.number })}</div> : null}
          </div>
        </div>
        <div>
          <Chip size="small" className={classes.planChip} label={row.planName || t("noPlan")} color={row.planValue > 0 ? "primary" : "default"} variant={row.planValue > 0 ? "default" : "outlined"} />
          <div className={classes.caption} style={{ marginTop: 4 }}>{row.monthlyValue > 0 ? `${usd(row.monthlyValue)}${t("perMonth")}` : t("free")}</div>
        </div>
        <div>
          <StateLine row={row} />
          <div className={classes.caption}>{t("dueOn", { date: dmy(row.dueDay) })}</div>
          {row.cycleInvoice ? <div className={classes.caption}>{t("invoiceOpen", { id: row.cycleInvoice.id, value: usd(row.cycleInvoice.value) })}</div> : null}
        </div>
        <LinesUsage row={row} />
        <div className={classes.actions}>
          {row.contact ? (
            <ChatBubbleButton title={t("openChat")} busy={busy} onClick={() => openChat({ contactId: row.contact.id, name: row.contact.name, number: row.contact.number })} />
          ) : null}
          <Button size="small" variant="contained" color="primary" startIcon={<PaymentIcon />} onClick={() => onPay(row)}>
            {t("registerPayment")}
          </Button>
          <IconButton size="small" aria-label={t("more")} onClick={(e) => setMenu(e.currentTarget)}><MoreVertIcon /></IconButton>
          <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
            <MenuItem onClick={() => { setMenu(null); onPlan(row); }}>{t("changePlan")}</MenuItem>
            <MenuItem onClick={() => { setMenu(null); onHide(row); }}>{t("hide")}</MenuItem>
          </Menu>
        </div>
      </div>
      {modal}
    </div>
  );
}

const isSuperUser = (user) =>
  Boolean(user && (user.profile === "super" || user.super === true || user.super === 1 || String(user.super) === "1"));

export default function WatoolxClientsPage() {
  const { user } = useContext(AuthContext);
  if (!isSuperUser(user)) {
    return (
      <PageFrame title={t("title")}>
        <EmptyHint title={t("onlySuper")} />
      </PageFrame>
    );
  }
  return <WatoolxClients />;
}

function WatoolxClients() {
  const negocio = useNegocioStyles();
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [query, setQuery] = useState("");
  const [paying, setPaying] = useState(null);
  const [planning, setPlanning] = useState(null);
  const [promo, setPromo] = useState(false);

  const toggleHidden = async (row, hidden) => {
    try {
      await api.put(`/platform/clients/${row.id}/hidden`, { hidden });
      toast.success(hidden ? t("hiddenDone", { name: row.name }) : t("shownDone", { name: row.name }));
      load();
    } catch (error) {
      toastError(error);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: next } = await api.get("/platform/clients");
      setData(next);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const companies = useMemo(() => data?.companies || [], [data]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, "");
    return companies
      .filter((r) => !state || (state === "live" ? r.state === "active" || r.state === "due_soon" : r.state === state))
      .filter((r) => !planFilter || r.planId === planFilter)
      .filter((r) => {
        if (!q) return true;
        const hay = `${r.name} ${r.email} ${r.contact?.name || ""} #${r.id}`.toLowerCase();
        return hay.includes(q) || (qDigits.length >= 3 && String(r.phone).replace(/\D/g, "").includes(qDigits));
      })
      .sort((a, b) => (STATE_ORDER[a.state] - STATE_ORDER[b.state]) || ((a.daysLeft ?? 99999) - (b.daysLeft ?? 99999)) || a.name.localeCompare(b.name));
  }, [companies, state, planFilter, query]);
  const pager = usePager(rows, 25);
  const totals = data?.totals;
  const count = (key) => companies.filter((r) => r.state === key).length;

  return (
    <PageFrame
      title={t("title")}
      subtitle={t("subtitle")}
      actions={
        <>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>{t("refresh")}</Button>
          <Button variant="contained" color="primary" startIcon={<AnnouncementIcon />} onClick={() => setPromo(true)} disabled={!data}>
            {t("promote")}
          </Button>
        </>
      }
    >
      <div className={negocio.kpiGrid}>
        <KpiTile icon={BusinessIcon} tone="kpiToneGood" label={t("kpi.live")} value={totals ? totals.active + totals.dueSoon : "…"} hint={totals ? <span className={negocio.kpiHint}>{t("kpi.ofTotal", { n: totals.total })}</span> : null} onClick={() => setState("live")} />
        <KpiTile icon={ScheduleIcon} tone="kpiToneWarn" label={t("kpi.dueSoon")} value={totals ? totals.dueSoon : "…"} onClick={() => setState("due_soon")} />
        <KpiTile icon={EventBusyIcon} tone="kpiToneBad" label={t("kpi.expired")} value={totals ? totals.expired : "…"} onClick={() => setState("expired")} />
        <KpiTile icon={CardGiftcardIcon} label={t("kpi.trial")} value={totals ? totals.trial : "…"} onClick={() => setState("trial")} />
        <KpiTile icon={AttachMoneyIcon} tone="kpiToneGood" label={t("kpi.revenue")} value={totals ? usd(totals.monthlyRevenue) : "…"} hint={totals ? <span className={negocio.kpiHint}>{t("kpi.paying", { n: totals.paying })}</span> : null} onClick={() => setState("")} />
      </div>

      {totals ? (
        <div className={classes.planBar}>
          {totals.byPlan.map((p) => (
            <Chip
              key={p.planId}
              size="small"
              clickable
              color={planFilter === p.planId ? "primary" : "default"}
              variant={planFilter === p.planId ? "default" : "outlined"}
              label={t("planCount", { name: p.name, n: p.live })}
              onClick={() => setPlanFilter(planFilter === p.planId ? "" : p.planId)}
            />
          ))}
        </div>
      ) : null}

      {data ? <StoreProductsCard data={data} onChanged={load} /> : null}
      {data && data.orders?.length ? <OrdersCard data={data} onChanged={load} /> : null}

      <div className={negocio.filterBar}>
        <FilterChips
          value={state}
          onChange={setState}
          options={[
            { value: "", label: `${tNegocio("filters.all")} (${companies.length})` },
            { value: "live", label: `${t("filter.live")} (${count("active") + count("due_soon")})` },
            { value: "due_soon", label: `${t("state.due_soon")} (${count("due_soon")})` },
            { value: "expired", label: `${t("state.expired")} (${count("expired")})` },
            { value: "trial", label: `${t("state.trial")} (${count("trial")})` },
            { value: "suspended", label: `${t("state.suspended")} (${count("suspended")})` },
          ]}
        />
      </div>
      <SearchField fullWidth value={query} onChange={setQuery} placeholder={t("search")} />

      <RecordsState loading={loading} count={rows.length} emptyTitle={t("empty")} emptySubtitle={companies.length ? t("emptyFiltered") : undefined}>
        <div className={`${negocio.tableWrap} ${negocio.tableWrapFill}`}>
          {pager.slice.map((row, index) => (
            <ClientRow key={row.id} row={row} index={index} onPay={setPaying} onPlan={setPlanning} onHide={(r) => toggleHidden(r, true)} />
          ))}
        </div>
        <NegocioPager count={pager.count} page={pager.page} rowsPerPage={pager.rowsPerPage} onChangePage={pager.setPage} onChangeRowsPerPage={pager.setRowsPerPage} hideBelow={25} />
      </RecordsState>

      {paying && data ? <PaymentDialog row={paying} data={data} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} /> : null}
      {planning && data ? <PlanDialog row={planning} plans={data.plans} onClose={() => setPlanning(null)} onDone={() => { setPlanning(null); load(); }} /> : null}
      {promo && data ? <PromoDialog data={data} onClose={() => setPromo(false)} /> : null}
      {data && data.hidden?.length ? (
        <div className={classes.hiddenBar}>
          <span>{t("hiddenList")}</span>
          {data.hidden.map((h) => (
            <Chip key={h.id} size="small" variant="outlined" label={`${h.name} #${h.id}`} onDelete={() => toggleHidden(h, false)} deleteIcon={<Tooltip title={t("show")}><VisibilityIcon /></Tooltip>} />
          ))}
        </div>
      ) : null}
    </PageFrame>
  );
}
